import React, { useEffect, useState } from 'react';
import {
  Button, 
  Typography, 
  CircularProgress, 
  FormControl, 
  Select, 
  MenuItem, 
  TextField, 
  Radio, 
  RadioGroup, 
  FormControlLabel, 
  Box, 
  DialogContent,
  Dialog,
  DialogTitle,
  DialogActions,
  Paper,
  FormLabel,
} from '@mui/material';
import Cropper from "react-easy-crop";
import Slider from "@mui/material/Slider";
import DialogContentText from "@mui/material/DialogContentText";

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckIcon from '@mui/icons-material/Check';
import { ListItemIcon, ListItemText } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { 
  getThemeAction, 
  getAllThemeAction, 
  setDefaultCustomerThemeAction,
  getThemeSelectedLogoAction 
} from '../../redux/actions/themeAction';
import {
  saveThemeAction,
  getThemesByDomainAction,
  addThemeToDomainAction,
  updateThemeAction,  
} from '../../redux/actions/themeActions';

import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const CustomThemeSettings = () => {
  const [mode, setMode] = useState('create'); // 'create' or 'update'
  const [selectedTheme, setSelectedTheme] = useState('');
  const [loadTheme, setLoadTheme] = useState('');
  const [themeOptions, setThemeOptions] = useState([]);
  const [domainId, setDomainId] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [reviewLogoUrl, setReviewLogoUrl] = useState('');
  const [primary, setPrimary] = useState(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [rawLogoFile, setRawLogoFile] = useState(null);
  const [rawLogoUrl, setRawLogoUrl] = useState("");

  const [leftColors, setLeftColors] = useState({
    primary: "#077BFD",
    secondary: "#000000",
  });
  const [rightColors, setRightColors] = useState({
    primary: "#077BFD",
    secondary: "#000000",
  });

  const [secondary, setSecondary] = useState(null);
  const [customTheme, setCustomTheme] = useState({
    primaryColor: "#077BFD",
    secondaryColor: "#000000",
    themeFont: "",
    fileName: "",
    fileType: "",
    fileSize: 0,
    fileData: null,
    fileUrl: "",
    name: "",
  });

  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user?.user);
  const theme = useSelector((state) => state?.theme?.theme);
  const all_themes = useSelector((state) => state?.theme?.all_themes || null);
 
  const selected_theme = theme?.themeName ?? '';
  const selected_theme_id = theme?.themeId ?? '';
  const loading = useSelector((state) =>
    state.loading?.GET_THEME ||
    state.loading?.SAVE_THEME ||
    state.loading?.UPDATE_THEME
  );

const mapToOptions = (arr) =>
  (Array.isArray(arr) ? arr : []).map((t) => ({
    id: String(t.id),
    name: t.name,
  }));


  
  const callbackfn = (res) => setThemeOptions(mapToOptions(res));

  useEffect(() => {
    dispatch(getThemeAction());
    dispatch(getAllThemeAction(callbackfn));
  }, [dispatch]);

  useEffect(() => {
    if (!selected_theme_id) return;
    (async () => {
      const res = await dispatch(getThemeSelectedLogoAction(selected_theme_id)); 
      const url = res?.payload?.signedUrl || res?.payload?.url || res?.signedUrl || res?.url || '';
      setLogoUrl(url);
    })();
  }, [dispatch, selected_theme_id]);


  useEffect(() => {
  if (!theme) return;

  const p = theme.primaryColor || "#ffae00";
  const s = theme.secondaryColor || "#000000";

  const themeId = String(theme.themeId ?? theme.id ?? "");
  const themeName = theme.themeName ?? theme.name ?? "";

  setCustomTheme(prevTheme => ({
    ...prevTheme,
    primaryColor: p,
    secondaryColor: s,
    themeFont: theme.themeFont || "",
    fileName: theme.fileName || "",
    fileType: theme.fileType || "",
    fileSize: theme.fileSize || 0,
    fileData: null,
    fileUrl: theme.fileUrl || "",
    name: themeName,
  }));

  setPrimary(p);
  setSecondary(s);
  setRightColors({ primary: p, secondary: s });

  setSelectedTheme(themeId);
  setLoadTheme(themeId);

  // 👇 NEW: load logo for right panel preview
  (async () => {
    try {
      const res = await dispatch(getThemeSelectedLogoAction(theme.themeId));
      const url =
        res?.payload?.signedUrl ||
        res?.payload?.url ||
        res?.signedUrl ||
        res?.url ||
        "";
      setReviewLogoUrl(url);
    } catch (e) {
      console.error("Error loading default theme logo", e);
      setReviewLogoUrl('');
    }
  })();
}, [theme, dispatch]);



  const handleLeftColorChange = (type, color) => {
    setLeftColors(prev => ({
      ...prev,
      [type === "primaryColor" ? "primary" : "secondary"]: color,
    }));
    setCustomTheme(prev => ({ ...prev, [type]: color }));
  };

  const handleRightColorChange = (type, color) => {
    setRightColors(prev => ({
      ...prev,
      [type === "primaryColor" ? "primary" : "secondary"]: color,
    }));
  };

  useEffect(() => {
    if (mode === 'update' && selectedTheme) {
      hydrateLeftFromTheme(selectedTheme);
    }
  }, [mode]);

 const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setRawLogoFile(file);

    const reader = new FileReader();
    reader.onload = (ev) => {
      setRawLogoUrl(ev.target.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropDialogOpen(true);
    };
    reader.readAsDataURL(file);
  };

const onCropComplete = (_croppedArea, croppedAreaPixels) => {
  setCroppedAreaPixels(croppedAreaPixels);
};

const handleCropSave = async () => {
  if (!rawLogoUrl || !croppedAreaPixels) {
    setCropDialogOpen(false);
    return;
  }

  try {
    const { blob, fileUrl } = await getCroppedImg(rawLogoUrl, croppedAreaPixels);

    const fileName = rawLogoFile?.name || "logo.png";
    const croppedFile = new File([blob], fileName, { type: "image/png" });

    setCustomTheme((prevTheme) => ({
      ...prevTheme,
      fileName: croppedFile.name,
      fileType: croppedFile.type,
      fileSize: croppedFile.size,
      fileData: croppedFile,
      fileUrl, // show cropped preview
    }));

    setCropDialogOpen(false);
  } catch (err) {
    console.error("Error cropping logo", err);
    setCropDialogOpen(false);
  }
};


  const handleSubmit = async () => {
    if (!user?.customer?.id || !user?.id) {
      alert("User/customer information missing.");
      return;
    }
    if (!customTheme.primaryColor || !customTheme.secondaryColor) {
      alert("Please select both primary and secondary colors.");
      return;
    }

    try {
      if (mode === 'create') {
        const name = customTheme.name.trim();
        if (!name) {
          alert("Please enter a theme name.");
          return;
        }
        const response = await dispatch(
          saveThemeAction(
            { ...customTheme, name },
            user,
            name,
            () => dispatch(getThemeAction(user.customer.id))
          )
        );
        if (response?.id && domainId) {
          await dispatch(addThemeToDomainAction(domainId, response.id));
          const themesResponse = await dispatch(getThemesByDomainAction(domainId));
          const themeArray =
            Array.isArray(themesResponse?.themes) ? themesResponse.themes :
            Array.isArray(themesResponse?.data) ? themesResponse.data :
            Array.isArray(themesResponse) ? themesResponse : [];
          setThemeOptions(mapToOptions(themeArray));
        }
      } else {
        // UPDATE EXISTING
        const id = selectedTheme;
        await dispatch(updateThemeAction(id, {
          primaryColor: customTheme.primaryColor,
          secondaryColor: customTheme.secondaryColor,
          themeFont: customTheme.themeFont,
          // if your API supports logo update, pass fileData/fileName as needed
          fileData: customTheme.fileData ?? undefined,
          fileName: customTheme.fileName ?? undefined,
        }));
        // refresh current theme & domain list
        await dispatch(getThemeAction(user.customer.id));
        if (domainId) {
          const themesResponse = await dispatch(getThemesByDomainAction(domainId));
          const themeArray =
            Array.isArray(themesResponse?.themes) ? themesResponse.themes :
            Array.isArray(themesResponse?.data) ? themesResponse.data :
            Array.isArray(themesResponse) ? themesResponse : [];
          setThemeOptions(mapToOptions(themeArray));
        }
      }
    } catch (error) {
      alert(error?.message || (mode === 'update' ? "Error updating theme." : "Error saving theme."));
    }
  };

  const resetTheme = async () => {
    setCustomTheme({
      primaryColor: "#ffae00",
      secondaryColor: "#000000",
      themeFont: "",
      fileName: "default.png",
      fileType: "image/png",
      fileSize: 0,
      fileData: null,
      fileUrl: "",
      name: "",
    });
    setSelectedTheme('');
    if (user?.customer?.id) {
      dispatch(getThemeAction(user.customer.id));
    }
  };

const setDefaultThemeHandler = async (e) => {
  const value = String(e.target.value);
  setLoadTheme(value);

  try {
    const res = await dispatch(getThemeSelectedLogoAction(value));
    const url =
      res?.payload?.signedUrl ||
      res?.payload?.url ||
      res?.signedUrl ||
      res?.url ||
      "";
    setReviewLogoUrl(url);
  } catch (e) {
    console.error("Error loading preview logo", e);
    setReviewLogoUrl('');
  }

  const picked = (all_themes ?? []).find(
    (t) => String(t.id) === value
  );

  const nextPrimary = picked?.primaryColor ?? "#ffae00";
  const nextSecondary = picked?.secondaryColor ?? "#000000";

  setRightColors({ primary: nextPrimary, secondary: nextSecondary });
  setPrimary(nextPrimary);
  setSecondary(nextSecondary);

  setCustomTheme((prev) => ({
    ...prev,
    primaryColor: nextPrimary,
    secondaryColor: nextSecondary,
  }));
};

 const updateThemeHandler = async () => {
  if (!loadTheme) return;

  try {
    await dispatch(setDefaultCustomerThemeAction(loadTheme));


    if (user?.customer?.id) {
      await dispatch(getThemeAction(user.customer.id));

    }

    const res = await dispatch(getThemeSelectedLogoAction(loadTheme));
    const url =
      res?.payload?.signedUrl ||
      res?.payload?.url ||
      res?.signedUrl ||
      res?.url ||
      '';
    setLogoUrl(url);

    const picked = (all_themes ?? []).find(
      t => String(t.id) === String(loadTheme)
    );
    const color = picked?.primaryColor ?? '#ffae00';
    const secondary = picked?.secondaryColor ?? '#000000';

    setRightColors({ primary: color, secondary });
    setCustomTheme(prev => ({
      ...prev,
      primaryColor: color,
      secondaryColor: secondary,
      fileUrl: url || prev.fileUrl,
    }));
  } catch (e) {
    console.error(e);
  }
};

const hydrateLeftFromTheme = async (id) => {
  const picked =
    (all_themes ?? []).find(t => String(t.id) === String(id)) ||
    (themeOptions ?? []).find(t => String(t.id) === String(id));

  const p = picked?.primaryColor ?? '#ffae00';
  const s = picked?.secondaryColor ?? '#000000';

  const res = await dispatch(getThemeSelectedLogoAction(id));
  const url =
    res?.payload?.signedUrl ||
    res?.payload?.url ||
    res?.signedUrl ||
    res?.url ||
    '';

  setLeftColors({ primary: p, secondary: s });

  setCustomTheme(prev => ({
    ...prev,
    primaryColor: p,
    secondaryColor: s,
    fileUrl: url || prev.fileUrl,  // left panel logo
    name: picked?.name ?? prev.name,
  }));

  setReviewLogoUrl(url);
};


  return (
    <Box sx={{ display: 'flex', gap: 4, p: 3 }}>
      {/* Left: Upload/Create Section */}
      <Paper sx={{ flex: 1, p: 3 }}>
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <RadioGroup
            row
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          >
            <FormControlLabel value="create" control={<Radio />} label="Create New" />
            <FormControlLabel value="update" control={<Radio />} label="Update Existing" />
          </RadioGroup>
        </FormControl>
        {mode === 'create' ? (
          <TextField
            label="New Theme Name"
            value={customTheme.name}
            onChange={(e) =>
              setCustomTheme(prev => ({
                ...prev,
                name: e.target.value
              }))
            }
            fullWidth
            sx={{ mb: 2 }}
          />
        ) : (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              labelId="theme-update-select-label"
              id="theme-update-select"
              value={selectedTheme}
               onChange={async (e) => {
                 const id = e.target.value;
                 setSelectedTheme(id);
                 await hydrateLeftFromTheme(id);
               }}
            >
             {themeOptions.map((opt) => (
               <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
             ))}
            </Select>
          </FormControl>
        )}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box>
            <Typography>Primary Color</Typography>
            <input
              type="color"
              value={leftColors.primary ?? '#077BFD'}
              onChange={(e) => handleLeftColorChange('primaryColor', e.target.value)}
              style={{ width: 40, height: 40, border: 'none', background: 'none' }}
            />
          </Box>
          <Box>
            <Typography>Secondary Color</Typography>
            <input
              type="color"
              value={leftColors.secondary ?? '#000000'}
              onChange={(e) => handleLeftColorChange('secondaryColor', e.target.value)}
              style={{ width: 40, height: 40, border: 'none', background: 'none' }}
            />
          </Box>
        </Box>
        <Box mb={2} sx={{display: 'flex', flexFlow: 'column'}}>
          <img
            src={customTheme.fileUrl || "path_to_default_image"}
            width="25%"
            alt="Logo Preview"
            style={{ marginBottom: 8 }}
          />

          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            sx={{ mt: 1, width:'250px' }}
            size="small"
          >
            Upload logo file here
            <VisuallyHiddenInput
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
            />
          </Button>
        </Box>
        <Box>
          <Button
            variant="outlined"
            onClick={resetTheme}
            disabled={loading}
          >
            Reset To Default
          </Button>
          
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ ml: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="secondary" /> : (mode === 'update' ? "Update Theme" : "Submit")}
          </Button>
        </Box>
      </Paper>
      {/* Right: Load Section */}
      <Paper sx={{ flex: 1, p: 3 }}>
        <FormLabel htmlFor='theme-load-select' variant="h5" mb={2}>Load Theme</FormLabel>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Select
            id="theme-load-select"
            value={loadTheme || ""}       // <-- string
            onChange={setDefaultThemeHandler}
            displayEmpty
          >
            <MenuItem value="">
              <em>Select a theme</em>
            </MenuItem>

            {themeOptions.map((opt) => {
              const isApplied = opt.name === selected_theme; // theme.themeName
              const isSelected = String(loadTheme) === String(opt.id);

              return (
                <MenuItem
                  key={opt.id}
                  value={opt.id}
                  sx={(theme) => ({
                    borderRadius: 1,
                    "&.Mui-selected": {
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.18) },
                    },
                    ...(isApplied && !isSelected
                      ? { bgcolor: alpha(theme.palette.success.main, 0.06) }
                      : {}),
                  })}
                >
                  {isApplied ? (
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon fontSize="small" />
                    </ListItemIcon>
                  ) : (
                    <ListItemIcon sx={{ minWidth: 32 }} />
                  )}

                  <ListItemText
                    primary={opt.name}
                    secondary={isApplied ? "Currently applied" : undefined}
                    primaryTypographyProps={{
                      fontWeight: isSelected ? 600 : 400,
                    }}
                  />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>


          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box>
              <Typography>Primary Color</Typography>
              <input
                type="color"
                value={rightColors.primary}
                onChange={(e) => handleRightColorChange("primaryColor", e.target.value)}
                style={{ width: 40, height: 40, border: "none", background: "none" }}
              />
            </Box>
            <Box>
              <Typography>Secondary Color</Typography>
              <input
                type="color"
                value={rightColors.secondary}
                onChange={(e) => handleRightColorChange("secondaryColor", e.target.value)}
                style={{ width: 40, height: 40, border: "none", background: "none" }}
              />
          </Box>
        </Box>

        <Box mb={2}>
        <img
          src={reviewLogoUrl || '/logo-placeholder.png'}
          alt="Logo Preview"
          width="25%"
          style={{ marginBottom: 8 }}
        />
      </Box>


        <Button variant='outlined' onClick={updateThemeHandler}>Set Default</Button>
      </Paper>

      <Dialog
        open={cropDialogOpen}
        onClose={() => setCropDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adjust Logo</DialogTitle>
        <DialogContent sx={{ position: "relative", height: 300, mt: 1 }}>
          {rawLogoUrl && (
            <Cropper
              image={rawLogoUrl}
              crop={crop}
              zoom={zoom}
              aspect={3} // adjust as you like
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              cropShape="rect"
              showGrid={false}
            />
          )}
        </DialogContent>
        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography gutterBottom variant="body2">
            Zoom
          </Typography>
          <Slider
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(_, value) => setZoom(value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCropDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCropSave}>
            Use this logo
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CustomThemeSettings;

async function getCroppedImg(imageSrc, cropPixels) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });

  const canvas = document.createElement("canvas");
  canvas.width = cropPixels.width;
  canvas.height = cropPixels.height;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    cropPixels.width,
    cropPixels.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const fileUrl = canvas.toDataURL("image/png");
      resolve({ blob, fileUrl });
    }, "image/png");
  });
}
